import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { SHARED_MODULES } from '../../../modules/shared-imports';
import { TranslatePipe } from 'src/app/i18n/translate.pipe';
import { TranslationService } from 'src/app/i18n/translation.service';
import { PermissionService } from 'src/app/services/permission.service';
import { GlobalSearchResult, GlobalSearchService } from '../../global-search/global-search.service';
import { NativeShareService } from '../../services/native-share.service';
import { ConsignmentDocType, PendingShare, ShareTarget, SharedFilesService } from '../../services/shared-files.service';

interface Tile {
  target: ShareTarget;
  icon: string;
  titleKey: string;
  hintKey: string;
  route: string;
  /** Rights the user needs — the same codes the destination's route guard / upload button check. */
  allOf: string[];
  anyOf?: string[];
}

interface DocTypeChip { value: ConsignmentDocType; labelKey: string; icon: string; }

/**
 * "Where should these files go?" — app-wide, mounted once in the logged-in shell (like the feedback host).
 * Opens when files are pasted anywhere with no upload button armed. It only shows places the user could
 * upload to today (hidden = no access; the destination screen and the server check again). It does NOT
 * upload: it routes to the destination, which attaches the files through its own upload code.
 */
@Component({
  selector: 'app-shared-files-sheet',
  standalone: true,
  imports: [CommonModule, SHARED_MODULES, TranslatePipe],
  templateUrl: './shared-files-sheet.component.html',
  styleUrls: ['./shared-files-sheet.component.css'],
})
export class SharedFilesSheetComponent implements OnInit, OnDestroy {
  private readonly shared = inject(SharedFilesService);
  private readonly perms = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly search = inject(GlobalSearchService);
  private readonly i18n = inject(TranslationService);
  private readonly native = inject(NativeShareService);

  readonly tiles: Tile[] = [
    { target: 'CONSIGNMENT', icon: 'pi-truck', titleKey: 'sfs.consignment', hintKey: 'sfs.consignmentHint',
      route: '/consignments/list',
      allOf: ['SHIPMENT_ACCESS'], anyOf: ['SHIPMENT_EDIT', 'SHIPMENT_ADD'] },
    { target: 'TALLY_DOCS', icon: 'pi-box', titleKey: 'sfs.stockUnit', hintKey: 'sfs.stockUnitHint',
      route: '/new-tallysheet/transport-unit/view',
      allOf: ['TALLY_ACCESS', 'TALLY_EDIT'] },
    { target: 'AI_IMPORT', icon: 'pi-sparkles', titleKey: 'sfs.aiImport', hintKey: 'sfs.aiImportHint',
      route: '/new-tallysheet',
      allOf: ['TALLY_ACCESS', 'TALLY_AI_IMPORT'] },
    { target: 'TICKET', icon: 'pi-ticket', titleKey: 'sfs.ticket', hintKey: 'sfs.ticketHint',
      route: '/support/tickets/new',
      allOf: ['SUPPORT_ACCESS'] },
  ];

  /** Same slot names the shipment screen uses; "Other" asks for a purpose there, as it always did. */
  readonly docTypes: DocTypeChip[] = [
    { value: 'BL', labelKey: 'BL File', icon: 'pi-file-pdf' },
    { value: 'Invoice', labelKey: 'Invoice', icon: 'pi-receipt' },
    { value: 'E-Waybill', labelKey: 'E-Waybill / LR File', icon: 'pi-file-word' },
    { value: 'Certificate of Origin', labelKey: 'Certificate of Origin', icon: 'pi-file-export' },
    { value: 'Phytosanitary', labelKey: 'Phytosanitary', icon: 'pi-leaf' },
    { value: 'Other', labelKey: 'Other Documents', icon: 'pi-folder-open' },
  ];

  visible = false;
  pending: PendingShare | null = null;
  pickingDocType = false;

  // Step 3 (Consignment, Stock Unit documents, AI Import): pick the record right here, then go straight to its
  // Documents / Photos / Tallysheet tab. The destination's own "Attach here" banner still does the real upload.
  pickingRecord: Tile | null = null;
  private recordDocType?: ConsignmentDocType;
  term = '';
  results: GlobalSearchResult[] = [];
  searching = false;
  searchFailed = false;
  private readonly term$ = new Subject<string>();
  private subs = new Subscription();
  private destroyed = false;

  ngOnInit(): void {
    // Restore files that were waiting before a reload FIRST, then (phone app) collect files shared from other apps
    // (WhatsApp, Files...) so the two are merged. `destroyed` covers a logout while the restore is still reading.
    void this.shared.registerHost().then(() => { if (!this.destroyed) void this.native.start(); });
    this.subs.add(this.shared.sheetOpen$.subscribe(open => {
      this.visible = open;
      if (!open) { this.pickingDocType = false; this.resetRecordPicker(); }
    }));
    this.subs.add(this.term$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      tap(term => { this.searchFailed = false; this.searching = term.trim().length >= 2; if (!this.searching) this.results = []; }),
      switchMap(term => term.trim().length < 2 ? of(null) : this.search.search(term.trim(), 8).pipe(
        catchError(() => { this.searchFailed = true; this.results = []; return of(null); }))),
    ).subscribe(res => {
      this.searching = false;
      const t = this.pickingRecord;
      if (!res || !t) return;
      this.results = ((t.target === 'CONSIGNMENT' ? res.shipments : res.transportUnits) ?? []).filter(r => !!r.encodedId);
    }));
    this.subs.add(this.shared.pending$.subscribe(p => (this.pending = p)));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subs.unsubscribe();
    this.native.stop();
    this.shared.unregisterHost();
  }

  /** Tiles the user is allowed to use. Empty = nothing to offer -> the "ask your admin" message. */
  get allowedTiles(): Tile[] {
    return this.tiles.filter(t =>
      t.allOf.every(c => this.perms.hasEffective(c)) &&
      (!t.anyOf || t.anyOf.some(c => this.perms.hasEffective(c))));
  }

  /** Why a tile can't take THESE files (greyed with a reason, never silently hidden). */
  disabledReason(t: Tile): string | null {
    const files = this.pending?.files ?? [];
    if (t.target === 'AI_IMPORT' && !files.some(f => f.type.startsWith('image/') || /\.pdf$/i.test(f.name) || f.type === 'application/pdf')) {
      return this.i18n.instant('sfs.aiNeedsImage');
    }
    return null;
  }

  pick(t: Tile): void {
    if (this.disabledReason(t)) return;
    if (t.target === 'CONSIGNMENT') { this.pickingDocType = true; return; }
    this.finish(t);
  }

  pickDocType(type: ConsignmentDocType): void {
    const t = this.tiles.find(x => x.target === 'CONSIGNMENT')!;
    this.finish(t, type);
  }

  /** Where the picked record is opened, straight on the tab that holds the "Attach here" banner. */
  private recordRoute(t: Tile, r: GlobalSearchResult): string {
    const id = encodeURIComponent(r.encodedId ?? '');
    switch (t.target) {
      case 'CONSIGNMENT': return `/consignments/${id}?tab=3`;                 // Documents tab of the consignment editor
      case 'TALLY_DOCS': return `/new-tallysheet?transportId=${id}#photos`;    // Photos tab of the Stock Unit editor
      default: return `/new-tallysheet?transportId=${id}#tallysheet`;          // AI Import: the tally grid (Tallysheet tab)
    }
  }

  pickRecord(r: GlobalSearchResult): void {
    const t = this.pickingRecord;
    if (!t || !r.encodedId) return;
    this.shared.choose(t.target, this.recordDocType);
    const route = this.recordRoute(t, r);
    this.resetRecordPicker();
    this.router.navigateByUrl(route);
  }

  onTerm(value: string): void {
    this.term = value;
    this.term$.next(value);
  }

  get recordTitleKey(): string {
    return this.pickingRecord?.target === 'CONSIGNMENT' ? 'sfs.pickConsignment' : 'sfs.pickStockUnit';
  }

  private resetRecordPicker(): void {
    this.pickingRecord = null;
    this.recordDocType = undefined;
    this.term = '';
    this.results = [];
    this.searching = false;
    this.searchFailed = false;
  }

  private finish(t: Tile, docType?: ConsignmentDocType): void {
    // The matching screen is already open and can take the files (saved Stock Unit, open consignment, new-ticket
    // page)? Stay there: it brings the "Attach here" banner forward and pulses it. No toast - it was easy to miss.
    this.shared.choose(t.target, docType);
    if (this.shared.openHere(t.target)) return;
    // A record is needed (Consignment, Stock Unit, AI Import): pick it here instead of hunting in a list.
    if (t.target !== 'TICKET') {
      // choose() closed the sheet and fixed the target; take that back and open the sheet again on the picker step.
      this.shared.undoChoose();
      this.recordDocType = docType;
      this.pickingDocType = false;
      this.pickingRecord = t;
      this.shared.reopenSheet();
      return;
    }
    this.router.navigateByUrl(t.route);
  }

  /** Phones: a bottom sheet, fixed to the screen (a centred dialog can sit below the fold of a long page). */
  get dialogPosition(): 'bottom' | 'center' {
    try { return window.matchMedia('(max-width: 640px)').matches ? 'bottom' : 'center'; } catch { return 'center'; }
  }

  back(): void {
    if (this.pickingRecord) {
      const wasConsignment = this.pickingRecord.target === 'CONSIGNMENT';
      this.resetRecordPicker();
      this.pickingDocType = wasConsignment;
      return;
    }
    this.pickingDocType = false;
  }
  remove(i: number): void { this.shared.removeFile(i); }
  cancel(): void { this.shared.discard(); }
  onHide(): void { if (this.pending && !this.pending.target) this.shared.discard(); }

  /** Floating "files are waiting" pill — visible on every screen while files are held for a screen. */
  get waitingHint(): string {
    switch (this.pending?.target) {
      case 'CONSIGNMENT': return this.i18n.instant('sfs.waiting.consignment');
      case 'TALLY_DOCS': return this.i18n.instant('sfs.waiting.stockUnit');
      case 'AI_IMPORT': return this.i18n.instant('sfs.waiting.aiImport');
      case 'TICKET': return this.i18n.instant('sfs.waiting.ticket');
      default: return '';
    }
  }

  icon(f: File): string {
    const t = (f.type || '').toLowerCase();
    if (t.startsWith('image/')) return 'pi-image';
    if (t === 'application/pdf') return 'pi-file-pdf';
    if (t.includes('excel') || t.includes('spreadsheet') || t === 'text/csv') return 'pi-file-excel';
    if (t.includes('word')) return 'pi-file-word';
    return 'pi-file';
  }

  size(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
